import services from 'services/module';
import {Strings} from 'Strings';
import {Reamaze} from 'reamaze';
import {CookieSettings} from 'services/CookieSettings';
import {HttpSvcFactory} from './HttpSvc';
import {NewsSvc} from './NewsSvc';
import {LocalStorageSvc} from './LocalStorageSvc';
import {Utils} from 'underscore-extensions';
import {AnalyticSvc} from './AnalyticSvc';

export class AuthenticationSvc {
    animatronCookieName    = '_animatronauth';
    signupRedirectPropName = 'signupRedirectUrl';
    loginRedirect          = '';
    loggedIn               = false;
    loaded                 = false;
    loginRedirectMessage   = '';
    signupModalShown       = false;
    loginModalShown        = false;
    lastUpdated            = _.now();
    reamaze                = new Reamaze(); //TODO do not depend on Reamaze

    constructor(private $rootScope: ng.IRootScopeService,
                private $state: ng.ui.IStateService,
                private $q: ng.IQService,
                private $timeout: ng.ITimeoutService,
                private $location: ng.ILocationService,
                private $log: ng.ILogService,
                private $cacheFactory: ng.ICacheFactoryService,
                private analyticSvc: AnalyticSvc,
                private ipCookie: ipCookie,
                private LocalStorageSvc: LocalStorageSvc,
                private HttpSvc: HttpSvcFactory,
                private NewsSvc: NewsSvc) { //TODO do not depend on NewsSvc
        $rootScope.updateUser = this.updateUser;
        $timeout(() => $rootScope.$on('$locationChangeStart', () => {
            this.loginRedirect = '';
            this.closeModals();
        }));
    }

    isCookieEnabled = _.once(() => {
        let enabled;
        const checkerCookieName = '__animatron_check_cookie_enabled';
        this.ipCookie(checkerCookieName, 'isEnabled');
        enabled = 'isEnabled' === this.ipCookie(checkerCookieName);
        this.ipCookie.remove(checkerCookieName);
        return enabled;
    });

    setUserGlobals(user: anm.User): anm.User {
        user.accountTypes        = {} as anm.UseraccountTypes;
        user.rolez              = user.subscriptionDetails.studio.features.rolez;
        user.meta               = user.meta || {};
        user.accountTypes.STUDIO = user.subscriptionDetails.studio.displayName;
        user.accountTypes.WAVE   = user.subscriptionDetails.wave.displayName;
        user.isStripe           = () => user.subscriptionDetails.studio.hasStripeSubscription || user.subscriptionDetails.studio.hasStripeUser;
        user.stripeKey          = () => user.zoneCode === 'kr' ? options.stripePubKeyKr : options.stripePubKey;
        this.$rootScope.user    = user;
        options.user            = user;
        options.headers         = {'Animatron-Security-Token': user.hash};
        this.NewsSvc.onUserChanged(user);
        this.loaded = true;
        return user;
    };

    setUser(user: anm.User, token: string): ng.IPromise<anm.User> {
        return this.$timeout(() => {
            this.loggedIn = true;
            this.setSubscription(user);
            user.hash      = token;
            user.staff     = user.subscriptionDetails.studio.name === 'Staff' || user.subscriptionDetails.studio.name === 'Jedi';
            user.anonymous = user.anonymous || user.username === 'Anonymous';
            if (user.staff) {
                options.debug = true;
            }
            if (window.Raven) {
                Raven.setUserContext({
                    id: user.username,
                    email: user.email,
                    subscription: user.subscriptionDetails.studio.displayName
                });
            }

            this.setUserGlobals(user);
            this.reamaze.setUser(user);
            this.LocalStorageSvc.set('user', user);
            return user;
        });
    };

    extendUser(newProps: any): void {
        this.$rootScope.user = angular.merge(options.user, newProps);
        this.LocalStorageSvc.set('user', options.user);
        this.notifyUserChange();
    };

    setAnonymous(): ng.IPromise<anm.User> {
        this.loggedIn = false;
        this.reamaze.setAnonymous();
        this.LocalStorageSvc.remove('user');
        return this.$timeout(() => this.setUserGlobals(options.anonymous));
    };

    getUser(update: boolean): ng.IPromise<anm.User> {
        const userCookieIsPresent = this.ipCookie(this.animatronCookieName);
        const cachedUser          = this.LocalStorageSvc.get<anm.User>('user');
        if (userCookieIsPresent && cachedUser) {
            if (update) {
                this.updateUser();
            }
            return this.setUser(cachedUser, cachedUser.hash);
        } else {
            return this.updateUser();
        }
    };

    updateUser(): ng.IPromise<anm.User> {
        const deferred  = this.$q.defer();
        const token     = this.ipCookie(this.animatronCookieName);
        options.headers = {'Animatron-Security-Token': token || ''};
        if (token) {
            this.HttpSvc('profile').secureGet().then(r => r.data).then(user => {
                this.lastUpdated = _.now();
                if (Utils.isUser(user)) {
                    this.setUser(user, token).then(deferred.resolve);
                } else {
                    this.clearCookies();
                    deferred.resolve(this.setAnonymous());
                }
            });
        } else {
            deferred.resolve(this.setAnonymous());
        }
        return deferred.promise;
    };

    login(secureUser: anm.SecureUser, loginType = 'Email'): ng.IPromise<anm.SecureUser> {
        try {
            this.ipCookie(this.animatronCookieName, secureUser.encryptedToken, CookieSettings.get());
            this.ipCookie.remove('_animatron_activate_pending', CookieSettings.get());
        } catch (e) {
            this.$log.error('Could not set cookie', e);
        }
        secureUser.userProfile.isNew  = secureUser.isNew;
        secureUser.userProfile.social = loginType.toLowerCase() !== 'email';
        return this.setUser(secureUser.userProfile, secureUser.encryptedToken).then(() => {
            this.closeModals();
            secureUser.isNew && this.analyticSvc.signUpSocialComplete(loginType as anm.SignUpProvider);
            this.analyticSvc.login(secureUser.userProfile);
            this.$rootScope.$emit('user:login');
            const thirdPartyRedirect = this.LocalStorageSvc.remove<string>('_animatron_redirect_onlogin');

            if (thirdPartyRedirect) {
                location.href = thirdPartyRedirect;
            } else if (this.loginRedirect === 'none') {
                // do nothing
            } else if (this.loginRedirect === 'back') {
                window.history.back();
            } else if (this.loginRedirect) {
                this.redirect(this.loginRedirect);
            } else if (this.shouldRedirect()) {
                this.redirectAfterLogin();
            }
            return secureUser;
        });
    };

    loginWithCookie(token: string): ng.IPromise<anm.User> {
        this.ipCookie(this.animatronCookieName, token, CookieSettings.get());
        return this.updateUser();
    };

    loginOAuth(authToken: string,
               oauthService: string,
               product: AnimatronProduct): ng.IPromise<anm.SecureUser> {
        const deferred  = this.$q.defer();
        const data: any = {'@class': 'oauth', authToken, oauthService, product: product.toUpperCase()};
        this.HttpSvc('authenticate').post(data, {
            headers: options.user.hash ? options.headers : {}
        }).then(r => r.data).then((response: ApiError | anm.SecureUser) => {
            if (Utils.isApiError(response)) {
                _.notification.showError(response.errorMessage);
            } else {
                this.loginRedirect = 'none';
                this.login(response, oauthService).then(deferred.resolve);
            }
        });
        return deferred.promise;
    };

    logout(): ng.IPromise<anm.User> {
        this.loginRedirect        = '';
        this.loginRedirectMessage = '';
        this.clearCookies();
        this.analyticSvc.logout();
        this.$cacheFactory.get('$http').removeAll();
        return this.setAnonymous();
    };

    logoutAndGoRoot() {
        const product = Utils.getProductByState(this.$state).toLowerCase();
        return this.logout().then(() => {
            if (this.shouldRedirect()) {
                this.$location.path(`/${product}/landing`).replace();
            } else {
                window.history.back();
            }
        });
    }

    clearCookies() {
        this.ipCookie.remove(this.animatronCookieName, CookieSettings.get());
    };

    notifyUserChange() {
        if (this.isCookieEnabled) {
            this.ipCookie('_animatronchanged', 'yes', CookieSettings.get());
        }
    };

    toggleLoginModal(redirect?: string, message = '') {
        this.loginRedirect        = redirect;
        this.loginRedirectMessage = message;
        this.loginModalShown      = true;
    };

    openSignUpPage(redirect?: string,
                   params?: {},
                   options?: ng.ui.IStateOptions) {
        const product = Utils.getProductByState(this.$state).toLowerCase();
        this.$state.go(`${product}-signup`, params, options);

        if (redirect) {
            this.LocalStorageSvc.set(this.signupRedirectPropName, redirect);
        }
    }

    activateEmailRedirect(): boolean {
        const url = this.LocalStorageSvc.remove<string>(this.signupRedirectPropName);

        if (url) {
            this.redirect(url);
        }

        return !!url;
    }

    openLogin() {
        if (options.handHeld) {
            this.$state.go('login').then(() => this.$timeout(() => this.loginRedirect = 'back'));
        } else {
            this.toggleLoginModal();
        }
    }

    closeModals() {
        this.signupModalShown = false;
        this.loginModalShown  = false;
    };

    private setSubscription(user: anm.User) {
        let wave: anm.SubscriptionDetails;
        let studio: anm.SubscriptionDetails;

        if (_.isArray(user.subscriptionDetails)) {
            user.subscriptionDetails.forEach((subscription: anm.SubscriptionDetails) => {
                if (subscription.product === 'WAVE') {
                    wave = subscription;
                } else {
                    studio = subscription;
                }
            });

            user.subscriptionDetails = Object.assign({}, {wave, studio});
        }
    }

    private shouldRedirect() {
        return !this.$location.search().noRedirect && !this.LocalStorageSvc.get('noRedirect');
    }

    private redirect(url: string) {
        Utils.redirect(url, this.$location);
    }

    private redirectAfterLogin() {
        const product = Utils.getProductByState(this.$state);
        const state   = product === 'WAVE' ? 'my-videos' : 'home';
        this.$state.go(state);
    }
}

services.service('AuthenticationSvc', AuthenticationSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/AuthenticationSvc.ts